import { FC, MouseEventHandler, useState, useEffect } from 'react';
import Icon from '@ant-design/icons';
import numeral from 'numeral';

import If from 'global/Common/If';
import { PaymentMethod } from 'global/Securities/types/transaction';
import { useTradingInfoStore } from 'global/Securities/stores/useTradingInfoStore';
import {
  TradingFeatureEnum,
  EventTypeEnum,
} from 'global/Securities/types/trading';
import { LightningSvg } from 'features/day-trade/components/Icons';
import {
  formatHourMinutes,
  replaceWords,
} from 'features/day-trade/utils/formater';

import Radio from 'global/Securities/components/Radio';
import Chips from 'global/Securities/components/Chips';
import SkeletonWrapper from 'global/Securities/components/SkeletonWrapper';
import ReloadSvg from 'global/Securities/components/Icons/ReloadSvg';
import BaseSecuritiesModal, {
  BaseSecuritiesModalProps,
} from 'global/Securities/components/Modal/BaseSecuritiesModal';
import CONSTANT from 'features/day-trade/constants/leverage';

import {
  clevertapDayTradeLeverageOptionViewed,
  clevertapDayTradeLeverageActivationSelected,
  clevertapSetVisibleBalance,
} from 'lib/analytics/securities';

import LeverageInfo from './leverage-info';

import {
  MethodDescription,
  MethodItem,
  MethodItemButton,
  MethodTextContent,
  MethodTitle,
  MethodGroup,
  MethodTitleSkeleton,
  MethodTitleWrapper,
  ChipsButton,
} from './leverage.styled';

interface PaymentMethodConfig {
  title: string;
  description: string;
}

type PaymentMethodWithoutTB = Exclude<
  PaymentMethod,
  PaymentMethod.TradingBalance
>;

type PaymentMethodConfigList = Record<
  PaymentMethodWithoutTB,
  PaymentMethodConfig
>;

interface PaymentMethodModalProps extends BaseSecuritiesModalProps {
  balance?: Record<PaymentMethodWithoutTB, number>;
  tradingBalance?: number;
  error?: Partial<Record<PaymentMethod, boolean>>;
  methodValue?: PaymentMethod;
  defaultMethodValue?: PaymentMethod;
  methodList?: PaymentMethodConfigList;
  loading?: boolean;
  symbol?: string;
  splitOrderActive?: boolean;
  dayTradeAvailable?: boolean;
  dayTradeMultiplier?: number;
  onMethodChange?(method: PaymentMethod): void;
  onReloadClick?(): void;
  onShowDayTradeOnboarding?(): void;
}

export const defaultMethodList: PaymentMethodConfigList = {
  TradingLimit: {
    title: 'Trading Limit',
    description: CONSTANT.TRADING_LIMIT.description,
  },
  DayTrade: {
    title: 'Day Trade',
    description: CONSTANT.DAY_TRADE.description,
  },
};

const defaultBalance = {
  [PaymentMethod.TradingLimit]: 0,
  [PaymentMethod.DayTrade]: 0,
};

const PaymentMethodModal: FC<PaymentMethodModalProps> = ({
  title = 'Leverage',
  balance = defaultBalance,
  methodList = defaultMethodList,
  methodValue,
  defaultMethodValue,
  tradingBalance = 0,
  loading = false,
  symbol = '',
  error = {},
  splitOrderActive = false,
  dayTradeAvailable = false,
  dayTradeMultiplier = 0,
  onMethodChange = () => {},
  onReloadClick = () => {},
  onShowDayTradeOnboarding = () => {},
  ...props
}) => {
  const [selectedMethod, setSelectedMethod] =
    useState<PaymentMethod>(defaultMethodValue);

  const { dayTrade, loadTradingInfo } = useTradingInfoStore((s) => ({
    dayTrade: s.dayTrade,
    loadTradingInfo: s.loadTradingInfo,
  }));

  const { event_type: eventType } = dayTrade || {};

  const handleMethodClick = (method: PaymentMethod) => {
    if (loading || error[method]) return;

    setSelectedMethod(method);
    onMethodChange(method);

    clevertapSetVisibleBalance();
  };

  const handleReloadClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    onReloadClick();
  };

  const handleOpenDayTradeActivation = () => {
    onShowDayTradeOnboarding();
    clevertapDayTradeLeverageActivationSelected();
  };

  const isDayTradeNotTradable = [
    EventTypeEnum.OUTSIDE_MARKET_HOURS,
    EventTypeEnum.EXCEED_DEBT_RATIO,
  ].includes(eventType);

  const getChipButton = (methodType: PaymentMethod) => {
    const isDayTrade = methodType === PaymentMethod.DayTrade;

    if (isDayTrade && (isDayTradeNotTradable || tradingBalance <= 0)) {
      return <Chips className="disabled-daytrade">Tidak Tersedia</Chips>;
    }

    const disabledChipsClassName =
      isDayTrade && balance[methodType] <= 0 ? 'disabled-daytrade' : '';

    return (
      <Chips
        data-cy={`${methodType}-balance`}
        className={disabledChipsClassName}
      >
        {`Rp${numeral(Math.floor(balance[methodType])).format('0,00')}`}
      </Chips>
    );
  };

  const getDefaultDescription = (description: string, isDayTrade = true) => {
    if (!isDayTrade) return description;

    const hourMinute = formatHourMinutes(
      dayTrade?.trading_open,
      dayTrade?.trading_close,
    );

    return replaceWords(description, ['%CLOSE_TIME%'], [hourMinute.close]);
  };

  const getDayTradeDescription = (daytradeBalance: number) => {
    if (tradingBalance <= 0) {
      return CONSTANT.DAY_TRADE_UNAVAILBALE_REASON.ZERO_BALANCE;
    }

    if (eventType === EventTypeEnum.OUTSIDE_MARKET_HOURS) {
      const hourMinute = formatHourMinutes(
        dayTrade.trading_open,
        dayTrade.trading_close,
      );

      return replaceWords(
        CONSTANT.DAY_TRADE_UNAVAILBALE_REASON.OUTSIDE_MARKET_HOURS,
        ['%CLOSE_TIME%', '%OPEN_TIME%'],
        [hourMinute.close, hourMinute.open],
      );
    }

    if (eventType === EventTypeEnum.EXCEED_DEBT_RATIO) {
      return CONSTANT.DAY_TRADE_UNAVAILBALE_REASON.EXCEED_DEBT_RATIO;
    }

    if (daytradeBalance <= 0) {
      return CONSTANT.DAY_TRADE_UNAVAILBALE_REASON.MAX_DEBT_LIMIT;
    }

    return getDefaultDescription(defaultMethodList.DayTrade.description);
  };

  // Only get trading info if day trade is available
  useEffect(() => {
    if (dayTradeAvailable && props.visible) {
      loadTradingInfo([TradingFeatureEnum.DAY_TRADE]);
    }
  }, [dayTradeAvailable, props.visible, loadTradingInfo]);

  useEffect(() => {
    if (props.visible) clevertapDayTradeLeverageOptionViewed();
  }, [props.visible]);

  return (
    <BaseSecuritiesModal title={title} hideSubmit {...props}>
      <MethodGroup value={methodValue || selectedMethod}>
        {Object.entries(methodList).map(
          ([methodType, methodConfig]: [
            PaymentMethod,
            PaymentMethodConfig,
          ]) => {
            if (balance[methodType] === undefined) return null;

            const isErrorOrLoading = loading || error[methodType];
            const isDayTrade = methodType === PaymentMethod.DayTrade;
            const isDayTradeAvailable = isDayTrade && dayTradeAvailable;
            const isDayTradeNotAvailable = isDayTrade && !dayTradeAvailable;
            const isDayTradeSplitOrderActive = isDayTrade && splitOrderActive;
            const isDayTradeExceedLimit =
              isDayTrade && balance[methodType] <= 0;
            const isDayTradeExceedLimitAndNotAvailable =
              isDayTrade && eventType === EventTypeEnum.EXCEED_DEBT_RATIO;
            const isDayTradeOutsideMarketHours =
              isDayTrade && eventType === EventTypeEnum.OUTSIDE_MARKET_HOURS;

            const isDisabledSelect =
              isErrorOrLoading ||
              isDayTradeNotAvailable ||
              isDayTradeSplitOrderActive ||
              isDayTradeExceedLimit ||
              isDayTradeExceedLimitAndNotAvailable ||
              isDayTradeOutsideMarketHours;

            return (
              <MethodItem
                key={methodType}
                data-cy={`${methodType}-button`}
                onClick={() => {
                  if (isDayTradeNotAvailable || isErrorOrLoading) {
                    return;
                  }

                  handleMethodClick(methodType);
                }}
                disabled={isDisabledSelect && dayTradeAvailable}
              >
                <MethodItemButton>
                  <MethodTextContent>
                    <MethodTitleWrapper>
                      <MethodTitle
                        disabled={isDisabledSelect && dayTradeAvailable}
                      >
                        {methodConfig.title}
                      </MethodTitle>

                      <If
                        condition={
                          isDayTrade &&
                          dayTradeMultiplier > 0 &&
                          (!isDisabledSelect || !isDayTradeAvailable)
                        }
                      >
                        <Chips
                          data-cy="day-trade-multiplier"
                          className="multiplier-chip"
                        >
                          <Icon component={LightningSvg} />

                          {`${dayTradeMultiplier}x`}
                        </Chips>
                      </If>

                      <SkeletonWrapper
                        skeleton={<MethodTitleSkeleton active />}
                        loading={loading}
                      >
                        <If condition={!isDayTrade || isDayTradeAvailable}>
                          <If condition={error[methodType] && !isDayTrade}>
                            <ChipsButton onClick={handleReloadClick}>
                              <Chips style={{ color: '#00AB6B' }}>
                                <Icon
                                  component={ReloadSvg}
                                  style={{ marginRight: 4 }}
                                />
                                Muat Ulang
                              </Chips>
                            </ChipsButton>

                            <If.Else>{getChipButton(methodType)}</If.Else>
                          </If>
                        </If>
                      </SkeletonWrapper>
                    </MethodTitleWrapper>

                    <MethodDescription>
                      <If condition={isDayTradeNotAvailable}>
                        <span>
                          Gunakan Day Trade dan dapatkan tambahan{' '}
                          {dayTradeMultiplier}x Buying Power saham {symbol}.
                        </span>

                        <If.Else>
                          <If condition={!isDisabledSelect}>
                            {getDefaultDescription(
                              methodConfig.description,
                              isDayTrade,
                            )}

                            <If.Else>
                              {getDayTradeDescription(balance[methodType])}
                            </If.Else>
                          </If>
                        </If.Else>
                      </If>
                    </MethodDescription>
                  </MethodTextContent>

                  <If condition={isDayTradeNotAvailable && !dayTradeAvailable}>
                    <Chips
                      className="activate-daytrade"
                      onClick={handleOpenDayTradeActivation}
                    >
                      Aktifkan
                    </Chips>

                    <If.Else>
                      <Radio
                        value={methodType as PaymentMethod}
                        disabled={isDisabledSelect}
                      />
                    </If.Else>
                  </If>
                </MethodItemButton>

                <If
                  condition={
                    isDayTrade &&
                    isDayTradeAvailable &&
                    !isDayTradeNotTradable &&
                    balance[methodType] > 0
                  }
                >
                  <LeverageInfo multiplier={dayTradeMultiplier} />
                </If>
              </MethodItem>
            );
          },
        )}
      </MethodGroup>
    </BaseSecuritiesModal>
  );
};

export default PaymentMethodModal;
